import type { DateTime } from "luxon";

const HOURS_PER_HALF_DAY = 12;
const HOURS_PER_DAY = 24;
const MINUTES_PER_HOUR = 60;

// Schedulers write "7p", "7:30a", "7 p.m." as often as "7pm" — a lone "a"/"p" must count as AM/PM, or "7p" silently
// becomes 7:00 AM.
const MERIDIEM_SUFFIX = /\s*([ap])\.?\s*(?:m\.?)?$/;

type TypedMeridiem = "a" | "p";

interface TypedTimeParts {
	digits: string;
	meridiem: TypedMeridiem | undefined;
	/** False when the text carries anything besides digits, ":", spaces and the AM/PM marker. */
	isTimeLike: boolean;
}

/** Splits typed text ("6p", "7:30 am", "1830") into its digits/colon part and an optional AM/PM marker. */
const splitTypedTime = (text: string): TypedTimeParts => {
	const input = text.trim().toLowerCase();
	const match = input.match(MERIDIEM_SUFFIX);
	const meridiem = match?.[1] as TypedMeridiem | undefined; // the regex only captures "a" or "p"
	const numericPart = match ? input.slice(0, match.index) : input;
	return { digits: numericPart.replace(/[^0-9:]/g, ""), meridiem, isTimeLike: /^[0-9:\s]*$/.test(numericPart) };
};

/**
 * Reads a time of day typed into a time field. Accepts 12-hour ("6p", "6pm", "7:30a", "730 p.m.") and 24-hour
 * ("18", "1830", "18:30") input. Anything after "(" is ignored, so an option label like "6:00 PM (12h0m)" parses too.
 * Returns undefined when the text is not a time.
 */
export const parseTypedTimeOfDay = (text: string): { hour: number; minute: number } | undefined => {
	const { digits, meridiem } = splitTypedTime(text.split("(")[0]);
	if (!digits) return;

	const [hoursStr, minutesStr] = digits.includes(":") ? digits.split(":") : digits.length <= 2 ? [digits, "0"] : [digits.slice(0, -2), digits.slice(-2)];
	const hours = Number.parseInt(hoursStr, 10);
	// DEFAULT: "7:" has no minutes yet — read it as on the hour, like "7".
	const minute = minutesStr ? Number.parseInt(minutesStr, 10) : 0;
	if (Number.isNaN(hours) || Number.isNaN(minute) || minute >= MINUTES_PER_HOUR) return;

	if (meridiem) {
		if (hours < 1 || hours > HOURS_PER_HALF_DAY) return;
		const hourOfHalfDay = hours % HOURS_PER_HALF_DAY;
		return { hour: meridiem === "p" ? hourOfHalfDay + HOURS_PER_HALF_DAY : hourOfHalfDay, minute };
	}
	if (hours >= HOURS_PER_DAY) return;
	return { hour: hours, minute };
};

const stripLeadingZero = (value: string): string => (value.startsWith("0") ? value.slice(1) : value);

/**
 * Whether a time option belongs in the list while the user is typing `text`. "6" keeps 6 AM and 6 PM, "6p" keeps
 * only the PM ones, "183" keeps 18:30.
 */
export const matchesTypedTime = (option: DateTime, text: string): boolean => {
	const { digits, meridiem, isTimeLike } = splitTypedTime(text);
	if (!isTimeLike) return false;
	const typedDigits = stripLeadingZero(digits.replace(/:/g, ""));
	const optionDigits12 = stripLeadingZero(option.toFormat("hhmm"));
	const optionDigits24 = stripLeadingZero(option.toFormat("HHmm"));

	if (meridiem) {
		return optionDigits12.startsWith(typedDigits) && option.toFormat("a").toLowerCase().startsWith(meridiem);
	}
	return optionDigits12.startsWith(typedDigits) || optionDigits24.startsWith(typedDigits);
};
